import React, { useEffect, useState } from "react";
import BranchSelect from "../../components/ui/BranchSelect";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../../components/layout/Layout";
import { getProfile } from "../../store/slices/authSlice";
import { widgetsApi } from "../../api/widgetsApi";
import { useDashboardApprovals } from "../../api/queries/approvalQueries";
import {
  useAttendanceToday,
  useBirthdays,
  useDashboardWidgets,
} from "../../api/queries/widgetQueries";
import { properName } from "../../utils/text";
import AttendanceToday, { OnLeaveModal } from "./AttendanceToday";
import useBranchTargets, {
  BRANCH_SCOPE_HINT,
} from "../../hooks/useBranchTargets";
import { useMyProfile } from "../../me/features/profile/useMyProfile";
import usePermissions from "../../hooks/usePermissions";
import {
  AnnouncementList,
  BirthdayList,
  BirthdaysModal,
  HolidayList,
} from "../../components/dashboard/WidgetLists";
import "./DashboardPage.css";

// "Recent hires" window (the old per-company tile said 3 months)
const RECENT_DAYS = 90;

const greeting = () => {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Manila",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date()),
  );
  return hour < 12
    ? "Good morning"
    : hour < 18
      ? "Good afternoon"
      : "Good evening";
};
const shortDay = (ymd) =>
  ymd
    ? new Date(`${ymd}T00:00:00`).toLocaleDateString("en-PH", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

/**
 * Compact KPI tile (icon left, label / number / caption right): a link when
 * `to` is given, a skeleton while loading, "—" with a retry on error.
 */
const KpiTile = ({
  label,
  value,
  caption,
  icon,
  color,
  to,
  onClick,
  q,
  children,
}) => {
  const body = (
    <>
      <span
        className="kpi-icon"
        style={{ "--tile-color": color }}
        aria-hidden="true"
      >
        <i className={`bi ${icon}`}></i>
      </span>
      <span className="kpi-text">
        <span className="kpi-label">{label}</span>
        {q?.isLoading ? (
          <span className="kpi-skeleton" aria-label="Loading"></span>
        ) : q?.isError ? (
          <span className="d-flex align-items-center gap-2">
            <span className="kpi-value">—</span>
            <button
              type="button"
              className="btn btn-sm btn-light border py-0"
              onClick={(e) => {
                e.preventDefault();
                q.refetch();
              }}
              aria-label={`Retry ${label}`}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </span>
        ) : (
          <span className="kpi-value">{value ?? 0}</span>
        )}
        {children ||
          (caption && <span className="kpi-caption">{caption}</span>)}
      </span>
    </>
  );
  if (to)
    return (
      <Link to={to} className="kpi-tile glass-tile is-link">
        {body}
      </Link>
    );
  if (onClick)
    return (
      <button
        type="button"
        className="kpi-tile glass-tile is-link text-start border-0 w-100"
        onClick={onClick}
      >
        {body}
      </button>
    );
  return <div className="kpi-tile glass-tile">{body}</div>;
};

/** A dashboard card: header, then a body that scrolls inside the card (null items = it failed). */
const WidgetPanel = ({ title, icon, action, q, items, children }) => (
  <section className="glass-panel dash-card">
    <header className="dash-card-head">
      <h2 className="dash-card-title">
        <i className={`bi ${icon} me-2 text-primary`}></i>
        {title}
      </h2>
      {action}
    </header>
    <div className="dash-card-body">
      {q.isLoading ? (
        <div
          className="kpi-skeleton kpi-skeleton--block"
          aria-label="Loading"
        ></div>
      ) : q.isError || items === null ? (
        <div className="d-flex align-items-center gap-2 my-2 fs-080 text-slate-500">
          Couldn&apos;t load this right now.
          <button
            type="button"
            className="btn btn-sm btn-light border"
            onClick={() => q.refetch()}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Retry
          </button>
        </div>
      ) : (
        children(items)
      )}
    </div>
  </section>
);

// Request types (GET /dashboard/approvals requests[]) → this portal's pages
const REQUEST_ROUTES = {
  CHECKIN_VALIDATION: {
    to: "/approvals?tab=validation",
    icon: "bi-patch-check",
  },
  OVERTIME: { to: "/approvals?tab=overtime", icon: "bi-clock-history" },
  CORRECTION: { to: "/approvals?tab=corrections", icon: "bi-pencil-square" },
  LEAVE: { to: "/leave/approvals", icon: "bi-calendar-event" },
  EVALUATION_TO_RATE: {
    to: "/performance/evaluations?status=PENDING_EVALUATION",
    icon: "bi-pencil-square",
  },
  EVALUATION_CONTESTED: {
    to: "/performance/evaluations?status=CONTESTED",
    icon: "bi-exclamation-octagon",
  },
};

/** Needs attention pills (zeros hidden), shown inside a KPI tile. */
const AttentionPills = ({ pending }) => {
  const items = pending.requests.filter((r) => r.count > 0);
  if (pending.isLoading) return null;
  if (!items.length)
    return (
      <span className="text-success fs-080 fw-semibold">
        <i className="bi bi-check-circle-fill me-1"></i>All caught up
      </span>
    );
  return (
    <span className="dash-pills">
      {items.map((r) => {
        const route = REQUEST_ROUTES[r.type] || {
          to: r.link || "/approvals",
          icon: "bi-inbox",
        };
        return (
          <Link
            key={r.type}
            to={route.to}
            className="dash-pill"
            title={r.label}
          >
            <i className={`bi ${route.icon}`}></i>
            <span className="dash-pill-label">{r.label}</span>
            <span className="dash-pill-count">{r.count}</span>
          </Link>
        );
      })}
    </span>
  );
};

/**
 * HR dashboard. Desktop (≥1200px): fits one screen; card bodies scroll, the
 * page doesn't. Tablet: 2 columns. Phones: one column, normal scrolling.
 */
const Dashboard = () => {
  const dispatch = useDispatch();
  const { user, accessToken, companyId, companyName } = useSelector(
    (state) => state.auth,
  );

  const { companyWide, can } = usePermissions();
  const employeeTiles = companyWide("employees");
  const allCompanies = employeeTiles && !companyId;
  const tileCompanyId = employeeTiles ? companyId : null;
  const canAnnounce = can("announcements", "modify");
  const canHolidays = can("holidays", "modify");
  const annTargets = useBranchTargets("announcements");
  const holTargets = useBranchTargets("holidays");
  // The dashboard's own branch filter ("" = all branches of the working
  // company); back to all branches when the working company changes
  const [dashBranchId, setDashBranchId] = useState("");
  useEffect(() => setDashBranchId(""), [companyId]);
  const scope = {
    companyId: tileCompanyId || companyId || undefined,
    branchId: dashBranchId || undefined,
  };
  const widgets = useDashboardWidgets({ scope });
  const pending = useDashboardApprovals(scope);
  const firstName = useMyProfile().data?.profile?.firstName;
  const [allBirthdays, setAllBirthdays] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const canAttendance = can("attendance");
  const canProfiles = can("employees");
  const attendanceScope = scope;
  const attendance = useAttendanceToday(attendanceScope, {
    enabled: canAttendance,
  });
  // The whole next 30 days (the widgets call only brings 5)
  const birthdays = useBirthdays(30, { scope });
  const birthdayItems = birthdays.isError
    ? widgets.data?.birthdays
    : birthdays.data;

  // Headcount: one call, works on "All companies" too (GET /dashboard/headcount)
  const headcount = useQuery({
    queryKey: [
      "dashboard",
      "headcount",
      tileCompanyId || null,
      dashBranchId || null,
    ],
    queryFn: () =>
      widgetsApi.headcount({
        companyId: tileCompanyId || undefined,
        branchId: dashBranchId || undefined,
        days: RECENT_DAYS,
      }),
    enabled: employeeTiles,
  });
  const since = headcount.data?.recentHiresSince;

  useEffect(() => {
    if (accessToken && !user) dispatch(getProfile());
  }, [dispatch, accessToken, user]);

  const name = properName(firstName) || user?.email?.split("@")[0];
  const today = new Date().toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <Layout>
      <div className="dash">
        {/* Header: one line on desktop */}
        <div className="dash-head">
          <div className="dash-head-main">
            <h1 className="dash-greeting">
              {greeting()}
              {name ? `, ${name}` : ""}
            </h1>
            <span className="text-muted fs-080">{today}</span>
            <span className="chip chip--sm chip--alt">
              <i className="bi bi-buildings me-1"></i>
              {allCompanies ? "All companies" : companyName || "My company"}
            </span>
            {/* Filters every card and tile; needs a company (not "All companies") */}
            <span className="dash-branch">
              <label htmlFor="dash-branch" className="visually-hidden">
                Branch
              </label>
              <BranchSelect
                id="dash-branch"
                companyId={companyId}
                value={dashBranchId}
                onChange={(v) => setDashBranchId(v || "")}
                emptyLabel="All branches"
                disabled={allCompanies}
              />
            </span>
          </div>
          {(canAnnounce || canHolidays) && (
            <div className="dash-actions">
              {canAnnounce && (
                <Link
                  to="/admin/announcements"
                  className={`btn btn-sm btn-light border ${annTargets.noneManaged ? "disabled" : ""}`}
                  aria-label="Post announcement"
                  aria-disabled={annTargets.noneManaged || undefined}
                  title={annTargets.noneManaged ? BRANCH_SCOPE_HINT : undefined}
                >
                  <i className="bi bi-megaphone"></i>
                  <span className="dash-action-label">Post announcement</span>
                </Link>
              )}
              {canHolidays && (
                <Link
                  to="/admin/holidays"
                  className={`btn btn-sm btn-light border ${holTargets.noneManaged ? "disabled" : ""}`}
                  aria-label="Add holiday"
                  aria-disabled={holTargets.noneManaged || undefined}
                  title={holTargets.noneManaged ? BRANCH_SCOPE_HINT : undefined}
                >
                  <i className="bi bi-calendar-plus"></i>
                  <span className="dash-action-label">Add holiday</span>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="dash-kpis">
          {employeeTiles && (
            <KpiTile
              label={allCompanies ? "Employees (all)" : "Employees"}
              value={headcount.data?.employees}
              caption="Active employees"
              icon="bi-people"
              color="#6366f1"
              to="/employee"
              q={headcount}
            />
          )}
          {employeeTiles && (
            <KpiTile
              label={`Hired · last ${RECENT_DAYS} days`}
              value={headcount.data?.recentHires}
              caption={since ? `Since ${shortDay(since)}` : "New hires"}
              icon="bi-person-plus"
              color="#10b981"
              to={since ? `/employee?hiredFrom=${since}` : "/employee"}
              q={headcount}
            />
          )}
          {/* Approvals + what is waiting, in one tile (pills are links) */}
          <div className="kpi-tile glass-tile">
            <Link
              to="/approvals"
              className="kpi-icon text-decoration-none"
              style={{ "--tile-color": "#f59e0b" }}
              aria-label="Open approvals"
            >
              <i className="bi bi-inbox"></i>
            </Link>
            <span className="kpi-text">
              <span className="kpi-label">Pending approvals</span>
              {pending.isLoading ? (
                <span className="kpi-skeleton" aria-label="Loading"></span>
              ) : (
                <Link
                  to="/approvals"
                  className="kpi-value text-decoration-none"
                >
                  {pending.total}
                </Link>
              )}
              <AttentionPills pending={pending} />
            </span>
          </div>
          {canAttendance && (
            <KpiTile
              label="On leave today"
              value={attendance.data?.onLeave}
              caption="See who"
              icon="bi-calendar-x"
              color="#8b5cf6"
              onClick={() => setLeaveOpen(true)}
              q={attendance}
            />
          )}
        </div>

        {/* Cards: fill the rest of the screen on desktop */}
        <div className={`dash-grid ${canAttendance ? "has-attendance" : ""}`}>
          {canAttendance && (
            <div className="dash-col dash-col--att">
              <AttendanceToday
                q={attendance}
                scope={attendanceScope}
                canOpenProfile={canProfiles}
              />
            </div>
          )}
          <div className="dash-col dash-col--bday">
            <WidgetPanel
              title="Birthdays"
              icon="bi-balloon"
              action={
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 fs-080 fw-semibold text-decoration-none"
                  onClick={() => setAllBirthdays(true)}
                >
                  View all
                </button>
              }
              q={birthdays.isError ? widgets : birthdays}
              items={birthdayItems}
            >
              {(items) => <BirthdayList items={items} />}
            </WidgetPanel>
          </div>
          <div className="dash-col dash-col--side">
            <WidgetPanel
              title="Announcements"
              icon="bi-megaphone"
              action={
                canAnnounce && (
                  <Link
                    to="/admin/announcements"
                    className="fs-080 fw-semibold text-decoration-none"
                  >
                    Manage
                  </Link>
                )
              }
              q={widgets}
              items={widgets.data?.announcements}
            >
              {(items) => <AnnouncementList items={items} />}
            </WidgetPanel>
            <WidgetPanel
              title="Holidays"
              icon="bi-calendar-heart"
              action={
                canHolidays && (
                  <Link
                    to="/admin/holidays"
                    className="fs-080 fw-semibold text-decoration-none"
                  >
                    Manage
                  </Link>
                )
              }
              q={widgets}
              items={widgets.data?.holidays}
            >
              {(items) => <HolidayList items={items} />}
            </WidgetPanel>
          </div>
        </div>
      </div>
      {leaveOpen && (
        <OnLeaveModal
          data={attendance.data}
          canOpenProfile={canProfiles}
          onClose={() => setLeaveOpen(false)}
        />
      )}
      {allBirthdays && (
        <BirthdaysModal scope={scope} onClose={() => setAllBirthdays(false)} />
      )}
    </Layout>
  );
};

export default Dashboard;
